import React from "react";
import { Calendar, CheckCircle2, Clock, FileText, ShieldCheck } from "lucide-react";

// The stat band above the results. Every figure is also the filter that produces
// it, so a number worth noticing is one click from being the list.
//
// The design's sixth tile, "Match my industries", is absent: nothing records
// which industries an organisation works in, so there is no honest number for
// it yet. A tile showing a made-up figure is worse than one fewer tile.

const compact = (n) => {
  if (n == null) return "—";
  if (n >= 10000) return `${(n / 1000).toFixed(1).replace(/\.0$/, "")}K`;
  return n.toLocaleString();
};

const TILES = [
  {
    key: "open",
    label: "Open tenders",
    Icon: FileText,
    tint: "#BB42AE",
    isOn: (f) => f.status === "open",
    apply: () => ({ status: "open" }),
  },
  {
    key: "closing_7d",
    // require_deadline rides along: the closing filter keeps tenders whose
    // deadline was never published, and this tile counted only dated ones.
    // Without it the tile read 4,738 and opened a list of 18,059.
    label: "Closing in 7 days",
    Icon: Clock,
    tint: "#DC2626",
    isOn: (f) => f.closing_within_days === 7 && f.require_deadline,
    apply: (f) =>
      f.closing_within_days === 7 && f.require_deadline
        ? { closing_within_days: null, require_deadline: false }
        : { closing_within_days: 7, require_deadline: true },
  },
  {
    key: "set_aside",
    label: "Set-aside eligible",
    Icon: ShieldCheck,
    tint: "#2563EB",
    isOn: (f) => f.set_aside_only,
    apply: (f) => ({ set_aside_only: !f.set_aside_only }),
  },
  {
    key: "posted_7d",
    label: "Posted this week",
    Icon: Calendar,
    tint: "#EA580C",
    isOn: (f) => f.posted_since_days === 7,
    apply: (f) => ({ posted_since_days: f.posted_since_days === 7 ? null : 7 }),
  },
  {
    key: "in_project",
    label: "Already in a project",
    Icon: CheckCircle2,
    tint: "#16A34A",
    isOn: (f) => f.only_imported,
    apply: (f) => ({ only_imported: !f.only_imported }),
  },
];

export default function TenderStats({ stats, filters, onChange, loading }) {
  return (
    <div
      className="grid bg-white border border-[#E5E7EB] rounded-[12px] overflow-hidden mb-[14px]"
      style={{ gridTemplateColumns: `repeat(${TILES.length}, minmax(0, 1fr))` }}
      role="group"
      data-tour="tender-stats"
      aria-label="Tender statistics"
    >
      {TILES.map((tile, i) => {
        const on = !loading && tile.isOn(filters);
        const { Icon } = tile;

        return (
          <button
            key={tile.key}
            type="button"
            aria-pressed={on}
            disabled={loading}
            onClick={() => onChange({ ...filters, ...tile.apply(filters) })}
            className={`relative text-left pt-[14px] px-4 pb-3 transition-colors ${
              i > 0 ? "border-l border-[#F1F1F3]" : ""
            } ${on ? "bg-[#F8F3FF]" : "bg-white hover:bg-[#FAFAFA]"}`}
          >
            {on && <span className="absolute left-0 right-0 bottom-0 h-[3px] bg-[#A04A97]" />}

            {loading ? (
              <>
                <div className="h-[26px] w-16 rounded bg-[#F4F4F5] animate-pulse" />
                <div className="h-3 w-24 rounded bg-[#F4F4F5] animate-pulse mt-2" />
              </>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <span className="font-outfit text-[26px] font-semibold leading-none text-[#18101C] tracking-[-0.02em]">
                    {compact(stats?.[tile.key])}
                  </span>
                  <Icon size={18} strokeWidth={1.8} color={tile.tint} />
                </div>
                <p
                  className={`font-outfit text-[12px] font-medium mt-2 ${
                    on ? "text-[#BB42AE]" : "text-[#71717A]"
                  }`}
                >
                  {tile.label}
                </p>
              </>
            )}
          </button>
        );
      })}
    </div>
  );
}
